import {getTestProps} from '@benchling/js-test-utils';
import {ActionIcon, Menu, Tooltip} from '@mantine/core';
import React from 'react';

import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

/**
 * Where an action prefers to render within the toolbar:
 * - `leading`: a distinct group at the start, set apart from the rest (e.g. Move).
 * - `main`: the primary group of quick actions.
 * - `overflow`: tucked inside the ⋮ menu.
 */
export type ToolbarActionPlacement = 'leading' | 'main' | 'overflow';

/**
 * The contract every toolbar-eligible action satisfies: a stable key, a placement, and a `render` callback.
 * Rendering, including any label or tooltip, belongs to the action, so composite controls (dropdowns) bring
 * their own and simple actions get theirs from {@link simpleToolbarAction}.
 *
 * `ActionToolbar` consumes a flat list of these and knows nothing else about the action.
 */
export interface ToolbarAction {
  /** Stable identity, used as the React key. */
  key: string;
  placement: ToolbarActionPlacement;
  /**
   * Renders the control for its placement. For `leading`/`main`, return a control (Button/ActionIcon or a
   * composite Dropdown). For `overflow`, return one or more `<Menu.Item>`s — they are rendered inside the
   * toolbar's overflow `<Menu.Dropdown>`.
   */
  render: () => React.ReactNode;
}

interface SimpleToolbarActionParams {
  key: string;
  /** The action's label: the icon button's accessible name and tooltip, or the overflow menu item's text. */
  name: string;
  icon: BenchlingIconName;
  onSelect: () => void;
  placement: ToolbarActionPlacement;
  isDisabled?: boolean;
  disabledTooltip?: string;
  /**
   * Styles the action in the destructive (red) palette in the overflow menu, e.g. Archive. Inline icon
   * buttons keep the toolbar's shared gray styling.
   */
  destructive?: boolean;
  dataTestAttrs?: DataTestAttrs;
}

/**
 * Convenience factory for the common single-click action (an icon plus a callback). Produces a
 * `ToolbarAction` whose `render` is an icon-only `ActionIcon` when leading/main, or a `Menu.Item` when in the
 * overflow menu. Actions that need bespoke rendering (dropdowns, submenus) construct a `ToolbarAction`
 * directly with their own `render`.
 */
export function simpleToolbarAction(params: SimpleToolbarActionParams): ToolbarAction {
  return {
    key: params.key,
    placement: params.placement,
    render: () => (params.placement === 'overflow' ? renderOverflowItem(params) : renderIconButton(params)),
  };
}

function renderIconButton(params: SimpleToolbarActionParams): React.ReactNode {
  const button = (
    <ActionIcon
      variant="tertiary"
      aria-label={params.name}
      disabled={params.isDisabled}
      onClick={params.onSelect}
      {...(params.dataTestAttrs ? getTestProps(params.dataTestAttrs) : {})}
    >
      <BenchlingIcon icon={params.icon} />
    </ActionIcon>
  );
  const label = params.isDisabled && params.disabledTooltip ? params.disabledTooltip : params.name;
  // A disabled ActionIcon does not emit pointer events, so wrap it in a div for the tooltip to fire.
  return (
    <Tooltip label={label} withinPortal={true}>
      {params.isDisabled ? <div>{button}</div> : button}
    </Tooltip>
  );
}

function renderOverflowItem(params: SimpleToolbarActionParams): React.ReactNode {
  const item = (
    <Menu.Item
      icon={<BenchlingIcon icon={params.icon} />}
      color={params.destructive ? 'red' : undefined}
      disabled={params.isDisabled}
      onClick={params.onSelect}
      {...(params.dataTestAttrs ? getTestProps(params.dataTestAttrs) : {})}
    >
      {params.name}
    </Menu.Item>
  );
  if (params.isDisabled && params.disabledTooltip) {
    return (
      <Tooltip label={params.disabledTooltip} position="right" withinPortal={true}>
        <div>{item}</div>
      </Tooltip>
    );
  }
  return item;
}
